import React, { useEffect, useState } from 'react';
import '../../styles/CommandCenterSidebar.css';
import '../../styles/CarrierDownloadSidebar.css';

interface SummaryPanel {
  sessions: {
    fully_consumed: number;
    partially_consumed: number;
    not_consumed: number;
    total_sessions: number;
  };
  rows: {
    direct_merged: number;
    ai_merged: number;
    ai_merge_candidate: number;
    user_action: number;
    total_rows: number;
  };
}

const DIST_COLORS = ['#8b5cf6', '#00b0d3', '#f59e0b', '#22c55e', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

function sortedEntries(data: Record<string, number>): [string, number][] {
  return Object.entries(data).sort((a, b) => b[1] - a[1]);
}

const DistributionSection: React.FC<{ title: string; data: Record<string, number> }> = ({ title, data }) => {
  const entries = sortedEntries(data);
  const sectionTotal = entries.reduce((sum, [, count]) => sum + count, 0);
  return (
    <div className="cdsb-section">
      <div className="cc-sb-section-lbl">{title}</div>
      <div className="cdsb-dist-card">
        {entries.length === 0 ? (
          <span className="cdsb-dist-empty">No data for this range</span>
        ) : (
          <>
            <div className="cdsb-dist-bar">
              {entries.map(([label, count], i) => (
                <div
                  key={label}
                  className="cdsb-dist-bar__seg"
                  style={{
                    width: `${sectionTotal > 0 ? (count / sectionTotal) * 100 : 0}%`,
                    background: DIST_COLORS[i % DIST_COLORS.length],
                  }}
                />
              ))}
            </div>
            {entries.map(([label, count], i) => (
              <div key={label} className="cdsb-dist-row">
                <span className="cdsb-dist-dot" style={{ background: DIST_COLORS[i % DIST_COLORS.length] }} />
                <span className="cdsb-dist-label">{label}</span>
                <span className="cdsb-dist-count">{count}</span>
                <span className="cdsb-dist-pct">{sectionTotal > 0 ? Math.round((count / sectionTotal) * 100) : 0}%</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const CarrierDownloadSidebarContent: React.FC = () => {
  const [summary, setSummary] = useState<SummaryPanel | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    const authToken = localStorage.getItem('authToken') || '';
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/carrier-downloads/summary-panel`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.text().catch(() => '');
          throw new Error(`${r.status} ${r.statusText}${body ? ` — ${body}` : ''}`);
        }
        return r.json();
      })
      .then((json: SummaryPanel) => setSummary(json))
      .catch((e) => setSummaryError(e.message || 'Failed to load summary panel'));
  }, []);

  return (
    <div className="cc-sb-dashboard">
      {summaryError && (
        <div className="cc-sb-section-lbl" style={{ color: '#ef4444', padding: '4px 0' }}>
          Summary panel error: {summaryError}
        </div>
      )}
      {summary && (
        <div className="cc-sb-stats">
          <div className="cc-sb-stat cc-sb-stat--purple">
            <div className="cc-sb-stat__body">
              <span className="cc-sb-stat__label">TOTAL SESSIONS</span>
              <span className="cc-sb-stat__context">All carrier download sessions</span>
            </div>
            <span className="cc-sb-stat__num cc-sb-stat__num--purple">{summary.sessions.total_sessions}</span>
          </div>
          <div className="cc-sb-stat cc-sb-stat--green">
            <div className="cc-sb-stat__body">
              <span className="cc-sb-stat__label">TOTAL SESSION ROWS</span>
              <span className="cc-sb-stat__context">All session rows</span>
            </div>
            <span className="cc-sb-stat__num cc-sb-stat__num--green">{summary.rows.total_rows}</span>
          </div>
        </div>
      )}

      {summary && (
        <>
          <DistributionSection title="Sessions" data={{
            'Fully Consumed': summary.sessions.fully_consumed,
            'Partially Consumed': summary.sessions.partially_consumed,
            'Not Consumed': summary.sessions.not_consumed,
          }} />
          <DistributionSection title="Session Rows" data={{
            'Direct Merged': summary.rows.direct_merged,
            'AI Merged': summary.rows.ai_merged,
            'AI Merge Candidate': summary.rows.ai_merge_candidate,
            'User Action': summary.rows.user_action,
          }} />
        </>
      )}
    </div>
  );
};

export default CarrierDownloadSidebarContent;
